import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Select,
  InputLabel, FormControl, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow,
  TablePagination, InputAdornment, IconButton, Tooltip,
  Collapse, Divider, Stack, Button
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  CheckCircle as CompletedIcon,
  Cancel as CancelledIcon,
  Schedule as TimeIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import axios from 'axios';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import SendInvoiceButton from '../components/admin/SendInvoiceButton';
import NotifyCustomerButton from '../components/admin/NotifyCustomerButton';
import PaymentStateChip from '../components/admin/PaymentStateChip';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const SERVICE_TYPE_LABELS = {
  regular:           'Regular',
  deep_clean:        'Deep Clean',
  move_in:           'Move In',
  move_out:          'Move Out',
  one_time:          'One Time',
  post_construction: 'Post Construction',
};

const STATUS_CONFIG = {
  NOT_STARTED: { label: 'Scheduled',   color: 'default', Icon: TimeIcon },
  IN_PROGRESS: { label: 'In progress', color: 'info',    Icon: TimeIcon },
  COMPLETED:   { label: 'Completed',   color: 'success', Icon: CompletedIcon },
  CANCELLED:   { label: 'Cancelled',   color: 'error',   Icon: CancelledIcon },
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDuration(minutes) {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m > 0 ? `${m}m` : ''}`.trim() : `${m}m`;
}

function formatMoney(cents) {
  if (!cents && cents !== 0) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatAddress(addr) {
  if (!addr) return '—';
  if (typeof addr === 'string') return addr;
  return [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', ');
}

// ── Row Detail Panel ─────────────────────────────────────────────────────────
const JobDetailRow = ({ job, colSpan }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ py: 0, bgcolor: 'grey.50' }}>
      <Collapse in timeout="auto">
        <Box px={3} py={2} display="flex" gap={4} flexWrap="wrap">
          {/* Address */}
          <Box minWidth={200}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Address</Typography>
            <Typography variant="body2">{formatAddress(job.address)}</Typography>
          </Box>

          {/* Maids */}
          <Box minWidth={160}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Team</Typography>
            {job.assignments?.length > 0 ? job.assignments.map(a => (
              <Box key={a.id} display="flex" alignItems="center" gap={0.5} mt={0.3}>
                <Typography variant="body2">
                  {a.maid ? `${a.maid.firstName} ${a.maid.lastName}` : 'Unknown'}
                </Typography>
                <Chip label={a.role} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                {a.performanceRating && (
                  <Box display="flex" alignItems="center" gap={0.2}>
                    <StarIcon sx={{ fontSize: 13, color: 'warning.main' }} />
                    <Typography variant="caption">{a.performanceRating}/5</Typography>
                  </Box>
                )}
              </Box>
            )) : <Typography variant="body2">—</Typography>}
          </Box>

          {/* Invoice */}
          <Box minWidth={140}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Invoice</Typography>
            <Box mt={0.5}>
              <SendInvoiceButton jobId={job.id} jobNumber={job.jobNumber} />
            </Box>
          </Box>

          {/* Customer notification */}
          <Box minWidth={140}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Customer</Typography>
            <Box mt={0.5}>
              <NotifyCustomerButton jobId={job.id} jobNumber={job.jobNumber} disabled={['COMPLETED', 'CANCELLED'].includes(job.status)} />
            </Box>
          </Box>

          {/* Timing */}
          <Box minWidth={160}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Timing</Typography>
            <Typography variant="body2">Estimated: {formatDuration(job.estimatedDuration)}</Typography>
            <Typography variant="body2">Actual: {formatDuration(job.actualDuration)}</Typography>
          </Box>

          {/* Task progress */}
          {job.totalTasks > 0 && (
            <Box minWidth={120}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Tasks</Typography>
              <Typography variant="body2">{job.completedTasks} / {job.totalTasks} completed</Typography>
            </Box>
          )}

          {/* Notes */}
          {job.completionNotes && (
            <Box minWidth={200} maxWidth={400}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Completion notes</Typography>
              <Typography variant="body2">{job.completionNotes}</Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </TableCell>
  </TableRow>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
const JobHistoryPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [invoiceStatuses, setInvoiceStatuses] = useState({});

  // Filters
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' = every status
  const [sortOrder, setSortOrder] = useState('desc');
  const [invoiceFilter, setInvoiceFilter] = useState(''); // '' | 'yes' | 'no'
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalJobs, setTotalJobs] = useState(0);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        sort: sortOrder,
        page: page + 1,
        limit: rowsPerPage,
      };
      if (statusFilter) params.statuses = statusFilter;
      if (invoiceFilter) params.invoiceSent = invoiceFilter;
      if (search) params.search = search;
      if (serviceTypeFilter) params.serviceType = serviceTypeFilter;
      if (paymentFilter) params.paymentStatus = paymentFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const res = await axios.get(`${API_URL}/api/jobs`, { params, headers: authHeaders() });
      const body = res.data;

      if (body.success) {
        setJobs(body.data.jobs);
        setTotalJobs(body.data.pagination.totalJobs);
        setPaymentSummary(body.data.paymentSummary || null);
      } else {
        setJobs([]);
        setTotalJobs(0);
      }
    } catch {
      setError('Failed to load job history.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter, sortOrder, invoiceFilter, serviceTypeFilter, paymentFilter, startDate, endDate]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Odoo invoice badge — display-only, degrades silently if the integration is off
  useEffect(() => {
    if (jobs.length === 0) { setInvoiceStatuses({}); return; }
    const jobIds = jobs.map(j => j.id).join(',');
    axios.get(`${API_URL}/api/admin/odoo/invoice-status`, { params: { jobIds }, headers: authHeaders() })
      .then(res => setInvoiceStatuses(res.data?.statuses || {}))
      .catch(() => setInvoiceStatuses({}));
  }, [jobs]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };

  const toggleRow = (id) => setExpandedRow(prev => prev === id ? null : id);

  const COL_COUNT = 9;

  return (
    <AdminPageLayout>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700}>Jobs</Typography>
        <Typography variant="body2" color="text.secondary">
          Every job — upcoming, in progress and past — with payment and invoice status.
        </Typography>
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" flexWrap="wrap" gap={2} alignItems="flex-end">
          {/* Search */}
          <TextField
            label="Customer name"
            size="small"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 200 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchInput
                    ? <IconButton size="small" onClick={handleClearSearch}><ClearIcon fontSize="small" /></IconButton>
                    : <IconButton size="small" onClick={handleSearch}><SearchIcon fontSize="small" /></IconButton>
                  }
                </InputAdornment>
              )
            }}
          />

          {/* Status */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={handleFilterChange(setStatusFilter)}>
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="NOT_STARTED">Scheduled (not started)</MenuItem>
              <MenuItem value="IN_PROGRESS">In progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
              <MenuItem value="COMPLETED,CANCELLED">Completed or cancelled</MenuItem>
            </Select>
          </FormControl>

          {/* Date order */}
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Order</InputLabel>
            <Select value={sortOrder} label="Order" onChange={handleFilterChange(setSortOrder)}>
              <MenuItem value="desc">Latest date first</MenuItem>
              <MenuItem value="asc">Earliest date first</MenuItem>
            </Select>
          </FormControl>

          {/* Service type */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Service type</InputLabel>
            <Select value={serviceTypeFilter} label="Service type" onChange={handleFilterChange(setServiceTypeFilter)}>
              <MenuItem value="">All types</MenuItem>
              {Object.entries(SERVICE_TYPE_LABELS).map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Payment */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Payment</InputLabel>
            <Select value={paymentFilter} label="Payment" onChange={handleFilterChange(setPaymentFilter)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="UNPAID">Unpaid</MenuItem>
              <MenuItem value="PAID">Paid</MenuItem>
              <MenuItem value="REFUNDED">Refunded</MenuItem>
            </Select>
          </FormControl>

          {/* Invoice */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Invoice</InputLabel>
            <Select value={invoiceFilter} label="Invoice" onChange={handleFilterChange(setInvoiceFilter)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="yes">Invoice sent</MenuItem>
              <MenuItem value="no">Not sent yet</MenuItem>
            </Select>
          </FormControl>

          {/* Date range */}
          <TextField
            label="From"
            type="date"
            size="small"
            value={startDate}
            onChange={handleFilterChange(setStartDate)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={endDate}
            onChange={handleFilterChange(setEndDate)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />

          {(search || statusFilter || serviceTypeFilter || paymentFilter || invoiceFilter || startDate || endDate) && (
            <Button size="small" onClick={() => {
              setSearchInput(''); setSearch(''); setStatusFilter(''); setInvoiceFilter('');
              setServiceTypeFilter(''); setPaymentFilter(''); setStartDate(''); setEndDate('');
              setPage(0);
            }}>
              Clear all
            </Button>
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Unpaid summary for the current filters */}
      {paymentSummary && paymentSummary.unpaidCount > 0 && (
        <Alert
          severity={paymentSummary.pastDueCount > 0 ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          action={paymentFilter !== 'UNPAID' && (
            <Button color="inherit" size="small" onClick={() => { setPaymentFilter('UNPAID'); setPage(0); }}>
              Show unpaid
            </Button>
          )}
        >
          <strong>{paymentSummary.unpaidCount} unpaid job{paymentSummary.unpaidCount === 1 ? '' : 's'}</strong>
          {' · '}{formatMoney(paymentSummary.unpaidAmount * 100)}
          {paymentSummary.pastDueCount > 0 && (
            <> — {paymentSummary.pastDueCount} past due ({formatMoney(paymentSummary.pastDueAmount * 100)})</>
          )}
        </Alert>
      )}

      {/* Table */}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
              <TableCell width={40} />
              <TableCell>Date</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Technicians</TableCell>
              <TableCell>Service type</TableCell>
              <TableCell align="right"><TimeIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> Duration</TableCell>
              <TableCell align="right"><MoneyIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Invoice</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT + 1} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT + 1} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No jobs found.</Typography>
                </TableCell>
              </TableRow>
            ) : jobs.map(job => {
              const isExpanded = expandedRow === job.id;
              const statusCfg = STATUS_CONFIG[job.status] || { label: job.status, color: 'default' };

              return (
                <React.Fragment key={job.id}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer', '& td': { borderBottom: isExpanded ? 'none' : undefined } }}
                    onClick={() => toggleRow(job.id)}
                  >
                    <TableCell sx={{ py: 0.5 }}>
                      <IconButton size="small">
                        {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{formatDate(job.scheduledDate)}</Typography>
                      <Typography variant="caption" color="text.secondary">{job.jobNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{job.customer?.name || '—'}</Typography>
                      {job.customer?.phone && (
                        <Typography variant="caption" color="text.secondary">{job.customer.phone}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {job.assignedMaids?.length > 0
                        ? job.assignedMaids.map((m, i) => (
                            <Typography key={i} variant="body2">{m}</Typography>
                          ))
                        : <Typography variant="body2" color="text.disabled">Unassigned</Typography>
                      }
                    </TableCell>
                    <TableCell>
                      {job.serviceType
                        ? <Chip label={SERVICE_TYPE_LABELS[job.serviceType] || job.serviceType} size="small" variant="outlined" />
                        : <Typography variant="body2" color="text.disabled">—</Typography>
                      }
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={job.estimatedDuration ? `Est. ${formatDuration(job.estimatedDuration)}` : ''}>
                        <Typography variant="body2">{formatDuration(job.actualDuration)}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500}>{formatMoney(job.totalAmount)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <PaymentStateChip state={job.paymentState} scheduledDate={job.scheduledDate} />
                        {job.paymentState === 'UNPAID' && job.invoiceSentAt && (
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            ✉ Sent {new Date(job.invoiceSentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </Typography>
                        )}
                        {job.paymentState === 'UNPAID' && (
                          <SendInvoiceButton jobId={job.id} jobNumber={job.jobNumber} compact sentAt={job.invoiceSentAt} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const inv = invoiceStatuses[job.id];
                        if (!inv) return <Typography variant="body2" color="text.disabled">—</Typography>;
                        if (inv.paymentState === 'paid' || inv.paymentState === 'in_payment') {
                          return <Chip label="Paid" color="success" size="small" variant="outlined" />;
                        }
                        if (inv.state === 'posted') {
                          return <Chip label="Posted" color="info" size="small" variant="outlined" />;
                        }
                        return <Chip label="Draft" color="default" size="small" variant="outlined" />;
                      })()}
                    </TableCell>
                  </TableRow>

                  {isExpanded && <JobDetailRow job={job} colSpan={COL_COUNT + 1} />}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>

        <Divider />
        <TablePagination
          component="div"
          count={totalJobs}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count} jobs`}
        />
      </Paper>
    </AdminPageLayout>
  );
};

export default JobHistoryPage;
